import React from "react";
import { Box, Typography } from "@mui/material";
import IntegrationApi, { type TheoOverviewQuery, type TheoOverviewSourceKind } from "../../apis/integrationApi";
import { NEUTRAL, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import type {
  TheoOverviewAgentTicketSource,
  TheoOverviewAssistedTicketSource,
  TheoOverviewConversationSource,
  TheoOverviewSourceList,
  TheoOverviewTimeSavedSource,
  TheoOverviewTimeSavedSources,
} from "../../types/integration";
import { formatCount, text } from "../monitor/SummaryPanel";
import SourceListDialog, { SourceColumn, useSourceList } from "./SourceListDialog";
import { SOURCE_COLORS, formatHours, resolverStateStyle, truncationNote } from "./theoOverviewModel";

const integrationApi = new IntegrationApi();

export type OverviewSourceKind = TheoOverviewSourceKind;

const linkSx = {
  ...text(13.5, VIOLET.theoText, 600),
  textDecoration: "none",
  borderRadius: `${RADIUS.control}px`,
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: "none", boxShadow: SELECTED_EDGE },
};
const mutedSx = text(12.5, NEUTRAL.textMuted);
const noteSx = { ...text(12.5, NEUTRAL.textMuted), mt: 1, "&:first-of-type": { mt: 0 } };

function formatWhen(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Opens in a new tab so the list stays open for the next check. */
const RecordLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
    {children}
  </Box>
);

const TicketCell: React.FC<{ ticketId: string; title: string | null }> = ({ ticketId, title }) => (
  <Box sx={{ minWidth: 220 }}>
    <RecordLink href={`/supervisor/tickets/${encodeURIComponent(ticketId)}`}>#{ticketId}</RecordLink>
    <Typography sx={{ ...text(13.5), mt: 0.25 }}>{title || "Untitled ticket"}</Typography>
  </Box>
);

const PeopleCell: React.FC<{ people: string[] }> = ({ people }) => (
  <Box sx={{ ...text(13.5), wordBreak: "break-all" }}>
    {people.length ? people.map((person) => <div key={person}>{person}</div>) : "—"}
  </Box>
);

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color, mr: 0.75 }}
  />
);

const NoneText: React.FC = () => (
  <Box component="span" sx={mutedSx}>
    None
  </Box>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => <Typography sx={noteSx}>{children}</Typography>;

function listSummary(list: TheoOverviewSourceList<unknown> | null, one: string, many: string): React.ReactNode {
  if (!list) return null;
  const count = `${formatCount(list.total)} ${list.total === 1 ? one : many}`;
  const note = truncationNote(list);
  return note ? `${count}. ${note}` : count;
}

interface SourceDialogProps {
  open: boolean;
  onClose: () => void;
  query: TheoOverviewQuery;
  requestKey: string;
  scopeLabel: string;
}

function useSource<T>(kind: OverviewSourceKind, { open, query, requestKey }: SourceDialogProps) {
  return useSourceList<T>(open, `${kind}|${requestKey}`, () => integrationApi.getTheoOverviewSources<T>(kind, query));
}

const AGENT_TICKET_COLUMNS: SourceColumn<TheoOverviewAgentTicketSource>[] = [
  { key: "ticket", header: "Ticket", render: (row) => <TicketCell ticketId={row.ticket_id} title={row.ticket_title} /> },
  { key: "customer", header: "Customer", render: (row) => row.customer_name || "—" },
  { key: "agent", header: "Agent", render: (row) => row.agent_name },
  {
    key: "state",
    header: "State",
    render: (row) => {
      const style = resolverStateStyle(row.resolver_state);
      return (
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          <Dot color={style.color} />
          {style.label}
        </Box>
      );
    },
  },
  { key: "type", header: "Ticket type", render: (row) => row.ticket_type || <NoneText /> },
  { key: "detected", header: "Picked up", numeric: true, render: (row) => formatWhen(row.detected_at) },
];

const AgentTicketsDialog: React.FC<SourceDialogProps> = (props) => {
  const state = useSource<TheoOverviewSourceList<TheoOverviewAgentTicketSource>>("agent-tickets", props);
  return (
    <SourceListDialog
      id="agent-ticket-sources"
      title="Tickets agents worked on their own"
      open={props.open}
      onClose={props.onClose}
      scopeLabel={props.scopeLabel}
      state={state}
      columns={AGENT_TICKET_COLUMNS}
      rowKey={(row) => `${row.agent_id}:${row.ticket_id}`}
      emptyText="No agent has worked a ticket in this view."
      summary={listSummary(state.data, "run", "runs")}
      footnote={
        <>
          <Note>
            One row per agent per ticket, from Auto Mode's run log: a ticket two agents worked in turn counts once for each.
            The window is when the agent picked the ticket up.
          </Note>
          <Note>State is the agent's own: waiting means it handed off or is waiting on a reply, not that the ticket is open.</Note>
        </>
      }
    />
  );
};

const ASSISTED_TICKET_COLUMNS: SourceColumn<TheoOverviewAssistedTicketSource>[] = [
  { key: "ticket", header: "Ticket", render: (row) => <TicketCell ticketId={row.ticket_id} title={row.ticket_title} /> },
  { key: "type", header: "Ticket type", render: (row) => row.ticket_type || <NoneText /> },
  { key: "technicians", header: "Technicians", render: (row) => <PeopleCell people={row.technicians} /> },
  { key: "prompts", header: "Prompts", numeric: true, render: (row) => formatCount(row.prompts) },
  { key: "last", header: "Last prompt", numeric: true, render: (row) => formatWhen(row.last_prompt_at) },
];

const AssistedTicketsDialog: React.FC<SourceDialogProps> = (props) => {
  const state = useSource<TheoOverviewSourceList<TheoOverviewAssistedTicketSource>>("assisted-tickets", props);
  return (
    <SourceListDialog
      id="assisted-ticket-sources"
      title="Tickets technicians prompted Theo on"
      open={props.open}
      onClose={props.onClose}
      scopeLabel={props.scopeLabel}
      state={state}
      columns={ASSISTED_TICKET_COLUMNS}
      rowKey={(row) => row.ticket_id}
      emptyText="No technician prompted Theo in a ticket in this view."
      summary={listSummary(state.data, "ticket", "tickets")}
      footnote={
        <>
          <Note>
            A ticket counts when someone typed to Theo in its ticket chat in this window. Prompts are the messages they typed;
            Theo's replies and Auto Mode's own turns aren't counted, and Theo staff are left out.
          </Note>
          <Note>Removed accounts and test IDs still appear here, as they do in the total.</Note>
        </>
      }
    />
  );
};

const CONVERSATION_COLUMNS: SourceColumn<TheoOverviewConversationSource>[] = [
  {
    key: "conversation",
    header: "Conversation",
    render: (row) => (
      <Box sx={{ minWidth: 220 }}>
        <RecordLink href={`/chat-assistant/${encodeURIComponent(String(row.ai_thread_id))}`}>
          {row.thread_name || "Untitled conversation"}
        </RecordLink>
        <Typography sx={{ ...mutedSx, mt: 0.25 }}>Thread {row.ai_thread_id}</Typography>
      </Box>
    ),
  },
  { key: "technicians", header: "Technicians", render: (row) => <PeopleCell people={row.technicians} /> },
  { key: "prompts", header: "Prompts", numeric: true, render: (row) => formatCount(row.prompts) },
  { key: "first", header: "Started", numeric: true, render: (row) => formatWhen(row.first_prompt_at) },
  { key: "last", header: "Last prompt", numeric: true, render: (row) => formatWhen(row.last_prompt_at) },
];

const ConversationsDialog: React.FC<SourceDialogProps> = (props) => {
  const state = useSource<TheoOverviewSourceList<TheoOverviewConversationSource>>("conversations", props);
  return (
    <SourceListDialog
      id="conversation-sources"
      title="Chat Assistant conversations"
      open={props.open}
      onClose={props.onClose}
      scopeLabel={props.scopeLabel}
      state={state}
      columns={CONVERSATION_COLUMNS}
      rowKey={(row) => String(row.ai_thread_id)}
      emptyText="No Chat Assistant conversation in this view."
      summary={listSummary(state.data, "conversation", "conversations")}
      footnote={
        <Note>
          Conversations with no ticket that someone typed in during this window, from Chat Assistant and Teams. Deleted
          conversations and Theo staff are left out.
        </Note>
      }
    />
  );
};

function timeSavedColumns(data: TheoOverviewTimeSavedSources | null): SourceColumn<TheoOverviewTimeSavedSource>[] {
  return [
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Box sx={{ minWidth: 220 }}>
          <Box component="code" sx={{ ...text(13, NEUTRAL.text, 600), fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {row.tool_name}
          </Box>
          <Typography sx={{ ...mutedSx, mt: 0.25 }}>{row.category_label}</Typography>
        </Box>
      ),
      total: "Total",
    },
    {
      key: "source",
      header: "Run by",
      render: (row) => (
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          <Dot color={SOURCE_COLORS[row.source] || NEUTRAL.borderStrong} />
          {row.source_label}
        </Box>
      ),
    },
    { key: "count", header: "Actions", numeric: true, render: (row) => formatCount(row.count), total: data ? formatCount(data.total) : null },
    { key: "each", header: "Minutes each", numeric: true, render: (row) => formatCount(row.minutes_each) },
    {
      key: "saved",
      header: "Time saved",
      numeric: true,
      render: (row) => (
        <Box component="span" sx={text(13.5, VIOLET.theoText, 700)}>
          {formatHours(row.estimated_minutes)}
        </Box>
      ),
      total: data ? formatHours(data.estimated_minutes) : null,
    },
  ];
}

const TimeSavedSourcesDialog: React.FC<SourceDialogProps & { onExplain: () => void }> = ({ onExplain, ...props }) => {
  const state = useSource<TheoOverviewTimeSavedSources>("time-saved", props);
  return (
    <SourceListDialog
      id="time-saved-sources"
      title="Actions behind the time saved"
      open={props.open}
      onClose={props.onClose}
      scopeLabel={props.scopeLabel}
      state={state}
      columns={timeSavedColumns(state.data)}
      rowKey={(row) => `${row.source}:${row.tool_name}`}
      emptyText="Theo hasn't completed any actions in this view."
      summary={state.data ? `${formatCount(state.data.total)} actions` : null}
      showTotals
      footnote={
        <>
          <Note>
            Every action Theo completed in this window, from its action audit log. Failed and cancelled actions count zero.
            Time saved is actions × minutes each.
          </Note>
          <Note>
            <Box
              component="button"
              type="button"
              onClick={onExplain}
              sx={{ ...linkSx, border: 0, background: "transparent", p: 0, cursor: "pointer", fontSize: 12.5, color: VIOLET.main }}
            >
              How minutes per action are set
            </Box>
          </Note>
        </>
      }
    />
  );
};

interface OverviewSourceDialogProps {
  /** The open list, or null when none is. */
  kind: OverviewSourceKind | null;
  onClose: () => void;
  query: TheoOverviewQuery;
  scopeLabel: string;
  onExplainTimeSaved: () => void;
}

/** The list behind whichever headline figure was clicked; each fetches its own records on open. */
const OverviewSourceDialog: React.FC<OverviewSourceDialogProps> = ({ kind, onClose, query, scopeLabel, onExplainTimeSaved }) => {
  const shared = {
    onClose,
    query,
    scopeLabel,
    requestKey: `${query.window}|${query.agentId || ""}|${query.customerId || ""}`,
  };
  return (
    <>
      <AgentTicketsDialog {...shared} open={kind === "agent-tickets"} />
      <AssistedTicketsDialog {...shared} open={kind === "assisted-tickets"} />
      <ConversationsDialog {...shared} open={kind === "conversations"} />
      <TimeSavedSourcesDialog {...shared} open={kind === "time-saved"} onExplain={onExplainTimeSaved} />
    </>
  );
};

export default OverviewSourceDialog;
